import {
  FilterChips,
  ListSection,
  PageHeader,
  PageHero,
  Screen
} from '@/shared/ui/Layout'
import {
  PageFilterChipsSkeleton,
  PageHeroSkeleton,
  PageListScreenSkeleton
} from '@/shared/ui/Skeleton'
import { RewardBadge } from '@/shared/ui/RewardBadge'

import { MissionListCard } from './components/MissionListCard'
import { MissionsListEmpty } from './components/MissionsListEmpty'
import { humanName } from './config'
import { useMissionsListView } from './hooks/useMissionsListView'

export const DigitalProductMissions = () => {
  const {
    filter,
    setFilter,
    items,
    visibleFilters,
    groups,
    featuredMission,
    globalProgress,
    isEmpty,
    isFilteredEmpty,
    isLoading
  } = useMissionsListView()

  return (
    <Screen>
      <Screen.Context className="mb-4">
        <PageHeader
          title={humanName.plural}
          subtitle="Complete missões e acumule recompensas."
        />

        {isLoading && <PageHeroSkeleton withProgress className="mb-0" />}

        {!isLoading && !isEmpty && (
          <PageHero
            art="/missions/target.png"
            backgroundImage="/missions/hero-bg.jpg"
            artFit="contain"
            className="mb-0"
          >
            <PageHero.Title>
              Complete missões e acumule recompensas
            </PageHero.Title>
            {(globalProgress.rewardScoreboard.totalPoints > 0 ||
              globalProgress.rewardScoreboard.totalCoins > 0) && (
              <PageHero.Subtitle>
                <RewardBadge.Group>
                  {globalProgress.rewardScoreboard.totalPoints > 0 && (
                    <RewardBadge
                      type="xp"
                      value={`${globalProgress.rewardScoreboard.earnedPoints}/${globalProgress.rewardScoreboard.totalPoints}`}
                      variant="inline"
                    />
                  )}
                  {globalProgress.rewardScoreboard.totalCoins > 0 && (
                    <RewardBadge
                      type="coins"
                      value={`${globalProgress.rewardScoreboard.earnedCoins}/${globalProgress.rewardScoreboard.totalCoins}`}
                      variant="inline"
                    />
                  )}
                </RewardBadge.Group>
              </PageHero.Subtitle>
            )}
            <PageHero.ProgressRow
              layout="stacked"
              value={globalProgress.percent}
              label={`${globalProgress.completed} de ${globalProgress.total} concluídas`}
            />
          </PageHero>
        )}
      </Screen.Context>

      <Screen.Sticky>
        {isLoading && <PageFilterChipsSkeleton />}
        {!isLoading && !isEmpty && (
          <FilterChips
            options={visibleFilters}
            value={filter}
            onChange={setFilter}
            aria-label="Filtrar missões"
          />
        )}
      </Screen.Sticky>

      <Screen.Content className="pt-4">
        {isLoading && (
          <PageListScreenSkeleton
            itemCount={3}
            aria-label="Carregando missões"
          />
        )}

        {!isLoading && isEmpty && <MissionsListEmpty />}

        {!isLoading && isFilteredEmpty && (
          <MissionsListEmpty filtered onClearFilter={() => setFilter('all')} />
        )}

        {/* Em "Todas": card em destaque acima das seções, quando o produtor
            marcou uma missão como destaque. O destaque sai do agrupamento
            (via `exclude` no hook) para não aparecer duas vezes. Ritmo por
            `gap-*` no container (MASTER L4). */}
        {!isLoading && !isEmpty && !isFilteredEmpty && filter === 'all' && (
          <div className="flex flex-col gap-5">
            {featuredMission && (
              <MissionListCard mission={featuredMission} featured />
            )}

            {groups.map(group => (
              <ListSection
                key={group.status}
                id={`missions-section-${group.status}`}
                title={group.label}
                count={group.items.length}
                status={group.status}
              >
                {group.items.map(mission => (
                  <MissionListCard key={mission.id} mission={mission} />
                ))}
              </ListSection>
            ))}
          </div>
        )}

        {!isLoading && !isEmpty && !isFilteredEmpty && filter !== 'all' && (
          <div className="flex flex-col gap-3">
            {items.map(mission => (
              <MissionListCard key={mission.id} mission={mission} />
            ))}
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <p
            className="text-center font-semibold text-gray-500"
            style={{
              fontSize: '0.75rem',
              letterSpacing: '0.02em',
              padding: '20px 0 40px'
            }}
          >
            Todos os desafios carregados
          </p>
        )}
      </Screen.Content>
    </Screen>
  )
}
